import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound,
  ArrowUpRight,
  Rocket,
  Braces,
  Bot,
  Terminal,
  Plug,
  GraduationCap,
  Clapperboard,
  Webhook,
} from 'lucide-react';

const DOCS_URL = 'https://developers.vivideo.ai';

/** Curated entry points into the developer docs (developers.vivideo.ai). */
const DOC_LINKS = [
  { key: 'quickstart', href: `${DOCS_URL}/quickstart`, Icon: Rocket },
  { key: 'reference', href: `${DOCS_URL}/reference`, Icon: Braces },
  { key: 'agents', href: `${DOCS_URL}/agents`, Icon: Bot },
  { key: 'cli', href: `${DOCS_URL}/cli`, Icon: Terminal },
  { key: 'mcp', href: `${DOCS_URL}/mcp`, Icon: Plug },
  { key: 'skills', href: `${DOCS_URL}/skills`, Icon: GraduationCap },
  { key: 'models', href: `${DOCS_URL}/models`, Icon: Clapperboard },
  { key: 'webhooks', href: `${DOCS_URL}/webhooks`, Icon: Webhook },
] as const;

export function DevelopersPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
      {/* Hero */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
          <span className="gradient-text">{t('developers.title')}</span>
        </h1>
        <p className="text-text-secondary max-w-2xl mx-auto mb-8 leading-relaxed">
          {t('developers.intro')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate('/account/api-keys')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            <KeyRound className="w-4 h-4" /> {t('developers.getApiKey')}
          </button>
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass text-sm font-semibold text-text-primary hover:bg-surface-light transition-colors"
          >
            {t('developers.exploreDocs')} <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Explore the docs */}
      <h2 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-4">
        {t('developers.exploreTitle')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        {DOC_LINKS.map(({ key, href, Icon }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="group glass rounded-2xl p-5 flex items-start gap-4 border border-glass-border hover:border-primary/40 transition-colors"
          >
            <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0 text-primary">
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-text-primary">
                  {t(`developers.cards.${key}.title`)}
                </h3>
                <ArrowUpRight className="w-3.5 h-3.5 text-text-tertiary group-hover:text-primary transition-colors" />
              </div>
              <p className="text-sm text-text-tertiary mt-0.5 leading-relaxed">
                {t(`developers.cards.${key}.desc`)}
              </p>
            </div>
          </a>
        ))}
      </div>

      {/* Get your API key */}
      <div className="glass rounded-2xl p-6 md:p-8 border border-primary/30 bg-primary/5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary to-accent flex items-center justify-center flex-shrink-0 text-white">
          <KeyRound className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-text-primary">
            {t('developers.apiKeyTitle')}
          </h3>
          <p className="text-sm text-text-secondary mt-1 leading-relaxed">
            {t('developers.apiKeyDesc')}
          </p>
        </div>
        <button
          onClick={() => navigate('/account/api-keys')}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-90 transition-opacity flex-shrink-0 whitespace-nowrap"
        >
          <KeyRound className="w-4 h-4" /> {t('developers.apiKeyCta')}
        </button>
      </div>
    </div>
  );
}
